import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Sparkles, Truck, ShieldCheck } from "lucide-react";
import { CategoriesStrip } from "../components/CategoriesStrip";
import { NewArrivalsGrid } from "../components/NewArrivalsGrid";
import { MiniMap } from "../components/MiniMap";
import CoverageChecker from "../components/CoverageChecker";
import { StoryBlurb } from "../components/StoryBlurb";
import { Testimonials } from "../components/Testimonials";

const HERO_RIKSHAW =
  "https://static.prod-images.emergentagent.com/jobs/7076c684-c2dc-44d5-b819-1abf0dd5cbeb/images/8c8ba3ed36d63125b29bd63442343935afc0b9c1caf46299317a0d708f2e77ea.png";

export default function Home() {
  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden" data-testid="home-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 md:pt-24 pb-20 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.22, 0.61, 0.36, 1] }}
            className="lg:col-span-6 space-y-8"
          >
            <span className="gold-divider label-overline text-jow-navy/70">
              An Indian Jewellery House on Wheels
            </span>
            <h1 className="font-display text-5xl md:text-6xl lg:text-7xl tracking-tight text-jow-navy leading-[1.05]">
              Beauty
              <span className="block italic text-jow-gold">that comes</span>
              to your doorstep.
            </h1>
            <p className="max-w-lg text-base md:text-lg text-jow-navy/70 leading-relaxed">
              Hand-curated artificial jewellery from India&rsquo;s finest karigars,
              delivered through our fleet of beautifully decorated e-rikshaws across
              six cities &mdash; from Delhi&rsquo;s lanes to Lucknow&rsquo;s chowks.
            </p>
            <div className="flex flex-wrap gap-4 pt-2">
              <Link to="/shop" data-testid="hero-cta-shop" className="btn-primary-jow">
                Shop Collection <ArrowUpRight size={16} />
              </Link>
              <Link to="/find-us" data-testid="hero-cta-find" className="btn-secondary-jow">
                Find a Rikshaw Near You
              </Link>
            </div>
            <div className="ornament-dots pt-4">
              <span></span>
              <span></span>
              <span></span>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.9, ease: [0.22, 0.61, 0.36, 1], delay: 0.1 }}
            className="lg:col-span-6 relative"
          >
            <div className="relative aspect-[4/5] overflow-hidden border border-jow-gold/40">
              <img
                src={HERO_RIKSHAW}
                alt="Decorated e-rikshaw with Indian bridal jewellery"
                className="absolute inset-0 w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-jow-navy/60 via-transparent to-transparent" />
              <div className="absolute bottom-6 left-6 right-6 text-jow-cream">
                <p className="label-overline text-jow-gold">Live in 6 cities</p>
                <p className="font-display text-2xl mt-2">
                  Delhi · Mumbai · Bangalore · Jaipur · Lucknow · Hyderabad
                </p>
              </div>
            </div>
            <div className="hidden md:block absolute -left-10 -bottom-10 w-44 h-44 border border-jow-gold/30" />
            <div className="hidden md:block absolute -right-6 -top-6 w-24 h-24 bg-jow-pink/15" />
          </motion.div>
        </div>
      </section>

      {/* TRUST STRIP */}
      <section className="border-y border-jow-navy/10 bg-white/60">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 grid grid-cols-1 md:grid-cols-3 gap-8">
          {[
            {
              icon: Sparkles,
              title: "Hand-curated",
              text: "Each piece, vetted by our atelier.",
            },
            {
              icon: Truck,
              title: "Delivered on a rikshaw",
              text: "Six-city e-rikshaw fleet, zero emissions.",
            },
            {
              icon: ShieldCheck,
              title: "Try before you pay",
              text: "Inspect at your door. Pay only if you love it.",
            },
          ].map((f) => (
            <div
              key={f.title}
              className="flex items-start gap-4"
              data-testid={`trust-${f.title.toLowerCase().replace(/\s+/g, "-")}`}
            >
              <span className="inline-flex w-11 h-11 items-center justify-center border border-jow-gold/50 text-jow-gold">
                <f.icon size={18} />
              </span>
              <div>
                <p className="font-display text-lg text-jow-navy">{f.title}</p>
                <p className="text-sm text-jow-navy/60">{f.text}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* SECTIONS */}
      <CategoriesStrip />
      <NewArrivalsGrid />
      <MiniMap />
      <CoverageChecker />
      <StoryBlurb />
      <Testimonials />
    </div>
  );
}
